import { useCallback, useEffect } from 'react';

import { useRetracked } from 'js/lib/retracked';
import user from 'js/lib/user';

type EventProperties = {
  focusEventName: string;
  visibilityEventName: string;
  extraTrackingData?: Record<string, unknown>;
};

/**
 * The hook sets up event listeners for the 'focus' and 'blur' events on the window object, and for the 'visibilitychange' event on the document object.
 * When these events occur, the hook calls the useRetracked hook with the appropriate event name, action, and tracking data.
 * The tracking data includes the user ID (retrieved from the user.get().id call) and any additional tracking data passed in the extraTrackingData parameter.
 * The action for the focus and blur events is 'focus' and 'blur', respectively.
 * The action for the visibility change event is either 'hide' or 'show', depending on whether the document is currently hidden.
 *
 * ```
 * useWindowFocusEventsTracking({
 *   extraTrackingData: { assignmentId: '1234' },
 *   focusEventName: 'window-focus',
 *   visibilityEventName: 'visibility-change',
 * });
 * ```
 *
 * @param focusEventName {string} the name of the event to be tracked when the window gains focus
 * @param visibilityEventName {string} the name of the event to be tracked when the window visibility changes
 * @param extraTrackingData {Record<string, unknown>} additional tracking data to be included in the tracking event
 * @return {void}
 *
 */
const useWindowFocusEventsTracking = ({ focusEventName, visibilityEventName, extraTrackingData }: EventProperties) => {
  const track = useRetracked();

  const onFocus = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(() => {
    track({
      trackingName: focusEventName,
      action: 'focus',
      trackingData: { userId: user.get().id, ...extraTrackingData },
    });
  }, [extraTrackingData, focusEventName, track]);

  const onBlur = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(() => {
    track({
      trackingName: focusEventName,
      action: 'blur',
      trackingData: { userId: user.get().id, ...extraTrackingData },
    });
  }, [extraTrackingData, focusEventName, track]);

  const onVisibilityChange = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(() => {
    const action = document.hidden ? 'hide' : 'show';
    track({
      trackingName: visibilityEventName,
      action,
      trackingData: { userId: user.get().id, ...extraTrackingData },
    });
  }, [extraTrackingData, track, visibilityEventName]);

  useEffect(() => {
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [onFocus]);

  useEffect(() => {
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('blur', onBlur);
    };
  }, [onBlur]);

  useEffect(() => {
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [onVisibilityChange]);
};

export default useWindowFocusEventsTracking;
